import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
import LazyLoad from 'react-lazyload';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import Faq from './components/faq';
import FinancialPlanningSection from './components/financialPlanningSection/financialPlanningSection';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection/introSection';
import Metadata from './components/metadata';
import ProductComparisonChart from './components/productComparisonChart';
import QuizCallout from './components/quizCallout';
import RelatedLinksSection from './components/relatedLinksSection';
import UnderstandingProduct from './components/understandingProduct';
// images
import heroImageDesktopImage from './components/heroSection/images/family-at-the-beach-desktop.webp';
import heroImageMobileImage from './components/heroSection/images/family-at-the-beach-mobile.webp';
// dislaimer footnotes
import {
  HighestFinancialStrengthRatings,
  LoyaltyBasedOnClientData,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      // Header inputs
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Investments',
          linkUrl: '/investments/',
          linkAriaLabel: 'Northwestern Mutual Investments',
        },
        {
          pageName: 'Fixed Variable Annuities',
          linkUrl: '/fixed-annuity-vs-variable-annuity/',
          linkAriaLabel: 'Northwestern Mutual Fixed Variable Annuities',
        },
      ]}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;All guarantees are backed by the claims&#45;paying ability of the issuer.
        </>,
        <>
          <sup>2</sup>&nbsp;No investment strategy can guarantee a profit or protect against loss. All investments carry some level of risk including the potential loss of principal invested. Financial Representatives do not give legal or tax advice. Taxpayers should seek advice based on their particular circumstances from an independent tax advisor
        </>,
        <>
          <sup>3</sup>&nbsp;Source: Michael Finke, Ph.D., CFP, chief financial officer at the American College of Financial Planning
        </>,
        <>
          {LoyaltyBasedOnClientData(4)}
        </>,
        <>
          {HighestFinancialStrengthRatings(5)} These ratings do not apply to the performance of the sub&#45;accounts within Northwestern Mutual&#39;s Variable Annuities.
        </>,
        <>
          Clients should refer to the appropriate disclosure brochure for important information on NMIS and&#47;or NMWMC, their services, fees and conflicts of interest before investing or engaging in financial planning services.
        </>,
        <>
          To be used with form numbers: ICC12.TT.CVUL.(0513), ICC12.TT.EVUL.(0513), ICC12.TT.SVUL.(0513), RR.VEL (0398), RR.VJL (1298), QQ.VCL, or equivalent. Not all contracts are available in all states.
        </>,
      ]}>

      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={heroImageMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={heroImageDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={heroImageDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <UnderstandingProduct />

      {/* QUIZ CALLOUT */}
      <LazyLoad
        offset={200}
        height={300}
        once
        placeholder={
          <section id='quiz-callout-fixed-variable-annuities-how-does-it-work'>
            <h2>How does an annuity work&#63; Test your knowledge.</h2>
          </section>
        }>
        <QuizCallout />
      </LazyLoad>

      <Faq />

      {/* IN PAGE LEAD FORM */}
      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id="in-page-lead-form" will be on the Form element itself.
          config={config}
          loadRecaptcha={loadRecaptcha}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          formLocation='/fixed-annuity-vs-variable-annuity/'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          formVariant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <ProductComparisonChart />

      <FinancialPlanningSection />

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        headingText='Explore more about annuities'
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

      <RelatedLinksSection />

    </Page>
  );
};
